import { Filter } from '../../../components/Filters/Filter';
import { useTranslation } from 'react-i18next';
import { PlatformChooser } from './PlatformChooser';
import { CountryChooser } from './CountriesChooser';
import { ContentTypeChooser } from './ContentTypeChooser';
import { AffiliationCountryChooser } from './AffiliationCountriesChooser';
import { AffiliationTypesChooser } from './AffiliationTypesChooser';
import { BotsChooser } from './BotsChooser';
import { IoCChooser } from './IoCChooser';
import { LanguageChooser } from './LanguageChooser';
import { FeedsChooser } from './FeedsChooser';
import { SourceChooser } from './SourceChooser';
import { SourceGroupChooser } from './SourceGroupChooser';
import { GeoGroupChooser } from './GeoGroupChooser';
import { TagsChooser } from './TagsChooser';
import { useSearchContext } from '../contexts';
import { getEmptyFilterState } from '../contexts';
import { useCurrentUser } from '../../../contexts/CurrentUser';

export const SearchPageFilter = () => {
  const { t } = useTranslation();
  const { state, updateDraftFilters, applyDraftFilters } = useSearchContext();
  const [currentUser] = useCurrentUser();
  const featureFlags: string[] = currentUser?.workspace?.config?.featureflags || [];
  const showTagsFilter = featureFlags.includes('show_tags_filter');

  // Mutual exclusivity logic for filters
  const getActiveExclusiveFilter = (): string | null => {
    if (state.draftFilters.sourceGroups.value.length > 0) return 'Actor groups';
    if (state.draftFilters.affiliations.value.length > 0 || state.draftFilters.affiliationTypes.value.length > 0) return 'State-affiliated';
    if (state.draftFilters.discreditations.value.length > 0) return 'High risk accounts';
    if (state.draftFilters.bots.value === true && state.draftFilters.bots.exclude === false) return 'Show only inauthentic actors';
    return null;
  };

  const activeExclusiveFilter = getActiveExclusiveFilter();

  const isFilterDisabled = (filterName: string): boolean => {
    return !!(activeExclusiveFilter && activeExclusiveFilter !== filterName);
  };

  const getDisabledTooltip = (filterName: string): string | undefined => {
    if (!isFilterDisabled(filterName) || !activeExclusiveFilter) return undefined;
    return t(`This filter can't be combined with '{{activeFilter}}'`, { activeFilter: t(activeExclusiveFilter) });
  };

  const filterContent = [
    {
      name: t('Platforms'),
      count: state.draftFilters.platforms.value.length,
      content: <PlatformChooser />,
      isExclude: state.draftFilters.platforms.exclude,
    },
    {
      name: t('Content types'),
      count: state.draftFilters.contentTypes.value.length,
      content: <ContentTypeChooser />,
      isExclude: state.draftFilters.contentTypes.exclude,
    },
    {
      name: t('Content languages'),
      count: state.draftFilters.languages.value.length,
      content: <LanguageChooser />,
      isExclude: state.draftFilters.languages.exclude,
    },
    {
      name: t('Actor origin country groups'),
      count: state.draftFilters.geoGroup.value.length,
      content: <GeoGroupChooser />,
      isExclude: state.draftFilters.geoGroup.exclude,
    },
    {
      name: t('Actor origin countries'),
      count: state.draftFilters.countries.value.length,
      content: <CountryChooser />,
      isExclude: state.draftFilters.countries.exclude,
    },
    {
      name: t('Actor groups'),
      count: state.draftFilters.sourceGroups.value.length,
      content: <SourceGroupChooser />,
      isExclude: state.draftFilters.sourceGroups.exclude,
      disabled: isFilterDisabled('Actor groups'),
      tooltipText: getDisabledTooltip('Actor groups'),
    },
    {
      name: t('Actors'),
      count: state.draftFilters.sources.value.length,
      content: <SourceChooser />,
      isExclude: state.draftFilters.sources.exclude,
    },
    {
      name: t('State-affiliated'),
      count: state.draftFilters.affiliations.value.length,
      content: <AffiliationCountryChooser />,
      isExclude: state.draftFilters.affiliations.exclude,
      disabled: isFilterDisabled('State-affiliated'),
      tooltipText: getDisabledTooltip('State-affiliated'),
    },
    {
      name: t('Affiliation Category'),
      count: state.draftFilters.affiliationTypes.value.length,
      content: <AffiliationTypesChooser />,
      isExclude: state.draftFilters.affiliationTypes.exclude,
      disabled: isFilterDisabled('State-affiliated'),
      tooltipText: getDisabledTooltip('State-affiliated'),
    },
    {
      name: t('High risk accounts'),
      count: state.draftFilters.discreditations.value.length,
      content: <IoCChooser />,
      isExclude: state.draftFilters.discreditations.exclude,
      disabled: isFilterDisabled('High risk accounts'),
      tooltipText: getDisabledTooltip('High risk accounts'),
    },
    {
      name: t('Inauthentic behavior'),
      count:
        state.draftFilters.bots.value === null
          ? 0
          : 1,
      content: <BotsChooser
        disableShowOnly={isFilterDisabled('Show only inauthentic actors')}
        disabledTooltip={getDisabledTooltip('Show only inauthentic actors')}
      />,
      isExclude: state.draftFilters.bots.exclude,
    },
    {
      name: t('Feeds'),
      count: state.draftFilters.feeds.value.length,
      content: <FeedsChooser />,
      isExclude: state.draftFilters.feeds.exclude,
    },
    ...(showTagsFilter
      ? [
          {
            name: t("Actor tags"),
            count: state.draftFilters.tags.value.length,
            content: <TagsChooser />,
            isExclude: state.draftFilters.tags.exclude,
          },
        ]
      : []),
  ];

  return (
    <Filter
      onClearAll={() => {
        const emptyFilters = getEmptyFilterState();
        updateDraftFilters(emptyFilters);
        applyDraftFilters();
      }}
      content={filterContent}
    />
  );
};
